import {
    FetchKeyedCollectionMixin,
    fetchStateKeyMatch
} from "@/shared/fetchStateHelper";
import { FetchState, FetchStateKey } from "@/shared/types";
import { ref, ToRefs } from "@vue/composition-api";
import { AxiosResponse } from "axios";
import { retryOnNotFoundError, RetryOptions } from "@/shared/retryHelper";
import Vue from "vue";

const emptyMixinState: FetchKeyedCollectionMixin<any> = {
    fetchKey: null,
    fetchStates: {},
    items: {}
};

export function applyRefToState<TResource extends object> ():
    ToRefs<TResource> {
    const reffedState: Partial<ToRefs<TResource>> = {};
    const emptyState = emptyMixinState;


    Object.keys(emptyState).forEach(propName => {
        reffedState[propName] = ref(emptyState[propName]);
    });

    return reffedState as ToRefs<TResource>;
}

export function addFetchStateMixin (): ToRefs<FetchKeyedCollectionMixin<any>> {
    const fetchStates = ref({});
    const fetchKey = ref(null as (null | FetchStateKey));
    const items = ref({});

    return {
        fetchStates,
        fetchKey,
        items,
    };
}

export function templatedFetchKeyCollectionStore<TResource extends object, TDto = TResource> (
    apiCall: (username: string, partnerId: number, args?: any) => Promise<AxiosResponse<TDto>>,
    retryOptions?: RetryOptions,
    retryOn404?: boolean
) {

    const state = applyRefToState();
    const mixin = addFetchStateMixin();

    function resetState () {
        mixin.fetchKey.value = null;
        mixin.fetchStates.value = {};
        mixin.items.value = {};
    }

    function setLoading ({ fetchKey, itemKey }: { fetchKey: FetchStateKey, itemKey: string }): void {
        mixin.fetchKey.value = fetchKey;
        Vue.set(mixin.fetchStates.value, itemKey, FetchState.Loading);
    }

    function setValueLoadedWithoutMapper ({ fetchKey, itemKey, value }:
                                              { fetchKey: FetchStateKey, itemKey: string | number, value: TDto }): void {
        if (!fetchStateKeyMatch(mixin.fetchKey.value, fetchKey)) {
            return; // request is stale
        }
        Vue.set(mixin.fetchStates.value, itemKey, FetchState.Loaded);
        Vue.set(mixin.items.value, itemKey, value);
    }

    const setValueLoaded = setValueLoadedWithoutMapper;

    function invalidateLoadedState (newFetchKey: FetchStateKey): void {
        if (fetchStateKeyMatch(mixin.fetchKey.value, newFetchKey)) {
            return;
        }

        resetState();
    }

    function fetchKeyedApiCall (
        partialFetchKey: Partial<FetchStateKey>,
        itemKey: string,
        args: any = undefined,
        force = false,
    ): Promise<AxiosResponse<TDto>> {

        partialFetchKey = partialFetchKey || {};
        const fetchKey: FetchStateKey = {
            username: partialFetchKey.username,
            partnerId: partialFetchKey.partnerId
        };

        invalidateLoadedState(fetchKey);
        if (!force && this.fetchStates[itemKey]) {
            return Promise.resolve(null);
        }


        setLoading({ fetchKey, itemKey });

        const actionResponse = retryOn404 ?
            retryOnNotFoundError(() =>
                    apiCall(fetchKey.username,
                        fetchKey.partnerId, args),
                retryOptions) : apiCall(fetchKey.username, fetchKey.partnerId, args);
        return actionResponse.then(response => {
            setValueLoaded({ fetchKey, itemKey, value: response.data });
            return response;
        });
    }

    return {
        ...state,
        ...mixin,
        resetState,
        $reset: resetState,
        setLoading,
        setValueLoaded,
        invalidateLoadedState,
        fetchKeyedApiCall,
    };
}
